<template>
  <div>
    <b-card no-body>
      <b-card-header class="pb-50">
        <div class="d-flex align-items-center" style="width: 100%;">
          <a class="filter-toggle mr-50" @click="collapsed = !collapsed" v-b-tooltip :title="$t('filters._title')">
            <i :class="collapsed ? 'fa-solid fa-sliders' : 'fa-solid fa-chevron-up'" style="font-size: 0.9rem"></i>
          </a>
          <!-- Action buttons sit right next to the expand toggle and stay
               visible whether the card is collapsed or expanded, so
               save/load/export/import/reset/refresh always work. (They used to
               live inside the collapsed-only row and vanished on expand, which
               is why saving filters stopped working.) -->
          <div class="d-flex align-items-center filter-actions mr-1">
            <a v-if="fileExport && hasPermission(permissions.EXPORT_EXCEL)" class="d-inline-block mr-1 filter-export" v-b-tooltip :title="$t('operations.export')"
               @click="$emit('export')"
            ><i class="fa-regular fa-file-excel"></i></a>
            <a v-if="fileImport" class="d-inline-block mr-1" v-b-tooltip :title="$t('operations.import')"
               @click="$emit('import')"
            ><i class="fas fa-file-upload"></i></a>
            <a id="load-query-bottom" class="d-inline-block mr-1" v-b-tooltip :title="$t('operations.load')"><i
                class="fa-solid fa-upload"
            ></i></a>
            <a id="save-query-bottom" class="d-inline-block mr-1" v-b-tooltip :title="$t('operations.save')"><i
                class="fa-regular fa-floppy-disk"
            ></i></a>
            <a id="reset-query-bottom" class="d-inline-block mr-1" v-b-tooltip :title="$t('operations.reset')"
               @click="resetFields"
            ><i class="fa-regular fa-x"></i></a>
            <a class="d-inline-block mr-1" @click="refreshTables"><i class="fa-solid fa-rotate-right"></i></a>
          </div>

          <div v-if="collapsed" style="flex: 1">
            <b-row :class="{ 'flex-nowrap': singleRow }">
              <b-col :cols="singleRow ? null : 3"  v-if="search">
                <b-form-input
                    size="sm"
                    v-model="filter.search"
                    :debounce="debounce"
                    :placeholder="$t('filters.search')"
                    class="flex-grow-1"
                />
              </b-col>
              <b-col :cols="singleRow ? null : 3" v-for="field in _fieldsOnCollapse" v-bind:key="field.key" >
                <slot :name="`filter(${field.key})`" :field="field" :filter="filter" >
                  <filter-card-fields-type-part v-model="filter" :organization-map="organizationMap" :field="field" :placeholder="$t(`filters.${field.label}`)"/>
                </slot>
              </b-col>
            </b-row>
          </div>

        </div>
        <div v-if="collapsed && activeFilters.length > 0" class="filter-tags mt-50">
          <b-badge
              v-for="af in activeFilters"
              :key="af.key"
              variant="light-primary"
              class="mr-50 mb-25 filter-tag"
          >
            {{ af.label }}: {{ af.value }}
            <a class="ml-25" @click.stop="clearFilter(af.key)"><i class="fa-solid fa-xmark" style="font-size: 0.65rem"></i></a>
          </b-badge>
        </div>
      </b-card-header>
      <b-collapse :visible="!collapsed">
      <b-card-body class="filter-card-body">
        <b-row v-if="fields != null && fields.length > 0" :class="{ 'flex-nowrap': singleRow }">
          <b-col :cols="singleRow ? null : 12" :md="singleRow ? null : 3" :lg="singleRow ? null : 3" v-for="field in _fields" v-bind:key="field.key" :class="{ 'pr-2': field.type === 'user', 'mr-0': field.type !== 'user' }">
            <label v-if="field.type !=='checkbox' && field.type !=='checkboxGroup'">{{
                $t(`filters.${field.label}`)
              }}</label>
            <slot :name="`filter(${field.key})`" :field="field" :filter="filter" >
              <filter-card-fields-type-part v-model="filter" :organization-map="organizationMap" :field="field"/>
            </slot>
          </b-col>
          <b-col cols="12" v-if="search">
            <label>{{ $t(`filters.search`) }}</label>
            <b-form-input size="sm" v-model="filter.search" :debounce="debounce" class="d-inline-block"/>
          </b-col>
        </b-row>
        <div class="text-center position-absolute d-block w-100" style="top:50%" v-else>
          <b-spinner>{{ $t('operations.loading') }}</b-spinner>
        </div>
      </b-card-body>
      </b-collapse>
    </b-card>
    <!-- Save query popover -->
    <b-popover target="save-query-bottom" placement="bottomleft" triggers="click" @show="closeAllPopovers">
      <div style="width: 350px">
        <p class="text-center text-muted" v-html="$t('filters.card.saveFilters')"></p>
        <b-input-group>
          <b-form-input v-model="popover.saveQuery.name" size="sm"></b-form-input>
          <b-input-group-append>
            <b-button size="sm" variant="outline-success" @click="saveQuery">{{ $t('operations.save') }}</b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </b-popover>

    <b-popover target="load-query-bottom" placement="bottomleft" triggers="click" @show="closeAllPopovers"
               @shown="loadAllQueries"
    >
      <div class="position-relative" style="width: 360px">
        <p class="text-center text-muted" v-html="$t('filters.card.loadFilters')"></p>
        <div>
          <v-select
              v-model="popover.loadQuery.selectedQueryIndex"
              :clearable="false"
              label="name"
              :options="popover.loadQuery.queries"
              :reduce="option => option.index"
          />
        </div>

        <b-row no-gutters style="width: 100%; margin-top: 5px">
          <b-col cols="6">
            <b-button class="d-inline-block" size="sm" variant="outline-danger" @click="deleteQuery"
                      :disabled="popover.loadQuery.selectedQueryIndex === null"
            >{{ $t('operations.delete') }}
            </b-button>
          </b-col>
          <b-col cols="6" class="text-right">
            <b-button class="d-inline-block" size="sm" variant="outline-success" @click="loadQuery"
                      :disabled="popover.loadQuery.selectedQueryIndex === null"
            >{{ $t('operations.load') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-popover>
  </div>
</template>

<script>
import { parseJson } from '@/model/Utils';
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox';
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox';
import CalendarPicker from '@/components/widget/picker/calendarPicker';
import CalendarPickerRange from '@/components/widget/picker/calendarPickerRange';
import UserSelectBox from '@/components/widget/select/UserSelectBox';
import mixinPopover from '@/components/mixin/mixinPopover';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import MonetaryStatusSelectBox from '@/components/widget/select/MonetaryStatusSelectBox';
import MonetaryActionSelectBox from '@/components/widget/select/MonetaryActionSelectBox';
import DepartmentsSelectBox from '@/components/widget/select/DepartmentsSelectBox';
import AffiliateSelectBox from '@/components/widget/select/AffiliateSelectBox.vue';
import SmtpProviderSelectBox from '@/components/widget/select/SmtpProviderSelectBox.vue';
import ActivitySelectBox from '@/components/widget/select/ActivitySelectBox.vue';
import ClientPicker from '@/components/widget/picker/clientPicker';
import MonetaryActionRealTypeSelectBox from '@/components/widget/select/type/monetaryActionRealTypeSelectBox';
import CampaignSelectBox from '@/components/widget/select/CampaignSelectBox.vue';
import StatusSelectBox from '@/components/widget/select/StatusSelectBox.vue';
import AccountPicker from '@/components/widget/picker/accountPicker.vue'
import FilterCardFieldsTypePart from '@/components/widget/FilterCardFieldsTypePart.vue'

export default {
  name: 'FilterCard',
  mixins: [mixinNotifications, mixinPopover, mixinPermissions],
  components: {
    FilterCardFieldsTypePart,
    AccountPicker,
    StatusSelectBox,
    ClientPicker,
    ActivitySelectBox,
    DepartmentsSelectBox,
    MonetaryStatusSelectBox,
    MonetaryActionSelectBox,
    MonetaryActionRealTypeSelectBox,
    SmtpProviderSelectBox,
    CalendarPickerRange,
    CalendarPicker,
    BusinessUnitSelectBox,
    CampaignSelectBox,
    OrganizationSelectBox,
    UserSelectBox,
    AffiliateSelectBox
  },
  data: () => ({
    collapsed: true,
    sessionKey: null,
    datePickerKey: 0,
    popover: {
      saveQuery: {
        name: '',
        error: ''
      },
      loadQuery: {
        queries: [],
        selectedQueryIndex: null,
      }
    }
  }),
  props: {
    value: {
      required: true,
    },
    loaded: {
      type: Boolean
    },
    fields: {
      required: true,
    },
    debounce: {
      default: () => 1500,
      type: Number
    },
    search: {
      default: () => false,
      type: Boolean
    },
    hiddenFields: {
      required: false,
      default: () => ({}),
      type: Object
    },
    fileExport: {
      default: () => false,
      type: Boolean
    },
    dashboard: {
      default: () => false,
      type: Boolean
    },
    // When true, all filter fields are laid out on a single (non-wrapping) row
    // with equal widths instead of the default 4-per-row grid.
    singleRow: {
      default: () => false,
      type: Boolean
    },
    fileImport: {
      default: () => false,
      type: Boolean
    },
    resetFieldsCallback: {
      type: Function,
      default: null
    }
  },
  watch: {
    value: {
      deep: true,
      handler(v) {
        sessionStorage.setItem(this.sessionKey, JSON.stringify(v));
      }
    }
  },
  created() {
    if (this.$route.meta.pageKey) {
      this.sessionKey = `${this.$route.name}::${this.$route.meta.pageKey}-filters`;
    } else {
      this.sessionKey = `${this.$route.name}-filters`;
    }
    this.initCache();
    this.$emit('loaded', true);
  },
  computed: {
    // Auto-inject a "desk" filter right before the (multi-select) organization
    // filter — or before the business-unit filter when there is no org filter —
    // so every filter card / dashboard that scopes by org gets a desk shortcut
    // without having to touch each *FilterFields.js file.
    fieldsWithDesk() {
      const fields = Array.isArray(this.fields) ? [...this.fields] : [];
      if (fields.length === 0 || fields.some(f => f && f.type === 'desk')) {
        return fields;
      }
      const isMulti = f => (f.props && f.props.multiple === true)
          || (f.selectProps && f.selectProps.multiple === true);
      let idx = fields.findIndex(f => f && f.type === 'org' && isMulti(f));
      if (idx < 0) {
        idx = fields.findIndex(f => f && f.type === 'bu' && isMulti(f));
      }
      if (idx < 0) {
        return fields;
      }
      const anchor = fields[idx];
      const deskField = {
        key: 'desks',
        type: 'desk',
        label: 'desk',
        props: { clearable: true, multiple: true },
        collapsed: anchor.collapsed === true,
        collapsedMinWidth: anchor.collapsedMinWidth,
      };
      const copy = [...fields];
      copy.splice(idx, 0, deskField);
      return copy;
    },
    _allFields() {
      let $this = this;
      return this.fieldsWithDesk.map(field => {
        if (field.type !== 'checkboxGroup') {
          return {
            key: field.key,
            label: field.label || field.key,
            type: field.type || 'input',
            debounce: field.debounce || $this.debounce || 1500,
            props: field.props,
            dateRange: field.dateRange,
            active: $this.hiddenFields == null || $this.hiddenFields[field.key] !== true,
            collapsed: field.collapsed === true,
            collapsedMinWidth: field.collapsedMinWidth,
          };
        } else {
          return {
            type: field.type,
            fields: field.fields.map(f => {
              return {
                key: f.key,
                label: f.label || f.key,
                debounce: f.debounce || $this.debounce || 1500,
                props: f.props,
                dateRange: f.dateRange,
                collapsed: field.collapsed === true,
                collapsedMinWidth: field.collapsedMinWidth
              };
            }),
            active: $this.hiddenFields == null || $this.hiddenFields[field.key] !== true,
          };
        }
      });
    },
    _fields() {
      return this._allFields.filter(field => field.active);
    },
    _fieldsOnCollapse() {
      return this._allFields.filter(field => field.active && field.collapsed);
    },
    filter: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      }
    },
    activeFilters() {
      if (!this.filter || !Array.isArray(this.fields)) return [];
      const ignoredKeys = ['search', 'lastUpdate', 'creationTime', 'ftdTime', 'buTypes'];
      const tags = [];
      this._allFields.forEach(field => {
        if (field.collapsed === true) return;
        if (field.type === 'checkboxGroup') {
          (field.fields || []).forEach(f => {
            if (this.filter[f.key] === true) {
              tags.push({ key: f.key, label: this.$t(`filters.${f.label}`), value: '✓' });
            }
          });
          return;
        }
        if(field.type === 'dateRange'){
          if(!this.filter[field.key] || !this.filter[field.key].start || !this.filter[field.key].end) return;
          const start = this.$moment.serverDate(this.filter[field.key].start).format(this.$t('timeFormat.dateTime.format'));
          const end = this.$moment.serverDate(this.filter[field.key].end).format(this.$t('timeFormat.dateTime.format'));
          tags.push({ key: field.key, label: this.$t(`filters.${field.label}`), value: `${start} - ${end}` });
          return;
        }
        const key = field.key;
        if (ignoredKeys.includes(key)) return;
        const val = this.filter[key];
        if (val == null || val === '' || val === false) return;
        if (Array.isArray(val) && val.length === 0) return;
        let display;
        if (Array.isArray(val)) {
          display = `(${val.length})`;
        } else if (typeof val === 'object') {
          display = val.name || val.label || JSON.stringify(val);
        } else if (typeof val === 'boolean') {
          display = '✓';
        } else {
          display = String(val);
        }
        tags.push({ key, label: this.$t(`filters.${field.label}`), value: display });
      });
      return tags;
    },
    organizationMap() {
      let $this = this;
      let map = {
        org: null,
        bu: null,
        desk: null,
        campaign: null,
        dep: null
      };
      if (!Array.isArray(this.fields)) {
        return map;
      }
      this._allFields.forEach(field => {
        if (field.type === 'org') {
          map.org = field.key;
        }
        if (field.type === 'bu') {
          map.bu = field.key;
        }
        if (field.type === 'desk') {
          map.desk = field.key;
        }
        if (field.type === 'campaign') {
          map.campaign = field.key;
        }
        if (field.type === 'department') {
          map.dep = field.key;
        }
      });
      return map;
    }
  },
  methods: {
    initCache() {
      this.filter = parseJson(sessionStorage.getItem(this.sessionKey), this.filter);
    },
    refreshTables() {
      if (this.dashboard) {
        this.$root.$emit('dashboard::refresh');
      } else {
        this.$root.$emit('system::refresh-tables');
      }
    },
    //Save query popover
    saveQuery() {
      let filter = { ...this.filter };
      delete filter.lastUpdate;
      delete filter.creationTime;
      delete filter.ftdTime;
      let queries = parseJson(localStorage.getItem(`${this.sessionKey}-filter-queries`), []);
      queries.push({
        name: this.popover.saveQuery.name,
        query: filter
      });
      localStorage.setItem(`${this.sessionKey}-filter-queries`, JSON.stringify(queries));
      this.popover.saveQuery.name = '';
      this.popover.saveQuery.error = '';
      this.$root.$emit('bv::hide::popover');
      this.showSuccessNotification(this.$t('filters.saveQuerySystem.querySaved'));
    },
    loadAllQueries() {
      let queries = parseJson(localStorage.getItem(`${this.sessionKey}-filter-queries`), []);
      queries = queries.map((q, index) => Object.assign(q, { index }));
      this.popover.loadQuery.queries = queries;
    },
    loadQuery() {
      if (this.popover.loadQuery.selectedQueryIndex == null || this.popover.loadQuery.selectedQueryIndex < 0) return;
      let $this = this;
      let lastUpdate = this.filter.lastUpdate;
      let query = this.popover.loadQuery.queries[this.popover.loadQuery.selectedQueryIndex].query;
      this.filter = {
        ...$this.filter, ...query,
        lastUpdate
      };
      this.forceUpdate();
      this.$root.$emit('bv::hide::popover');
    },
    deleteQuery() {
      if (
          this.popover.loadQuery.selectedQueryIndex == null ||
          this.popover.loadQuery.selectedQueryIndex < 0
      ) return;

      let queries = this.popover.loadQuery.queries;

      queries.splice(this.popover.loadQuery.selectedQueryIndex, 1);

      localStorage.setItem(
          `${this.sessionKey}-filter-queries`,
          JSON.stringify(queries)
      );

      this.popover.loadQuery.selectedQueryIndex = null;

      this.$root.$emit('bv::hide::popover');
      this.showSuccessNotification(this.$t('filters.card.queryDeleted'));
    },
    clearFilter(key) {
      this.$set(this.filter, key, Array.isArray(this.filter[key]) ? [] : null);
    },
    resetFields() {
      if(this.resetFieldsCallback && typeof this.resetFieldsCallback === 'function'){
        this.resetFieldsCallback();
        return;
      }
      // Keep buTypes safe
      const buTypes = this.filter.buTypes;

      // Reset all filter fields except buTypes
      Object.keys(this.filter).forEach(key => {
        if (key !== 'buTypes') {
          this.$set(this.filter, key, Array.isArray(this.filter[key]) ? [] : null);
        }
      });

      // Restore buTypes (in case it was missing)
      this.$set(this.filter, 'buTypes', buTypes);

      // Force date picker refresh if needed
      this.datePickerKey += 1;
    }


  }
};
</script>

<style scoped>
.filter-card-body {
  padding: 0.5rem 1rem;
}
.filter-card-body label {
  margin-bottom: 0;
  font-size: 0.85rem;
}
.filter-toggle {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}
/* Excel export — green pill so it stands out from the other filter actions */
.filter-export {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: linear-gradient(135deg, #28c76f, #1f9d57);
  color: #fff !important;
  box-shadow: 0 3px 8px -3px rgba(40, 199, 111, 0.65);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.filter-export:hover {
  color: #fff !important;
  transform: translateY(-1px);
  box-shadow: 0 5px 12px -4px rgba(40, 199, 111, 0.75);
}
.filter-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.filter-tag {
  font-weight: 400;
  font-size: 0.8rem;
  cursor: default;
}
.filter-tag a {
  cursor: pointer;
  opacity: 0.7;
}
.filter-tag a:hover {
  opacity: 1;
}
</style>
